import type {
  ArcTemplateDefinition,
  AspectRequirement,
  BaseStageAction,
  StageDefinition,
  StageIntent,
  KeyDateDefinition,
  StageActionDefinition,
  TargetResolver,
  TransitionDefinition,
} from 'types/arc';
import type { Schema } from 'types/schema';
import type { AutomationTier } from 'types/authority';
import type { UnifiedFilters } from 'types/filters';
import { classifyApplicability } from '../marketplace/classifyApplicability';
import { slugifyAspectName } from '../../api/aspectApi';

/**
 * The lane key of the grid's Unattributed row and the sentinel `PerspectiveMultiPickerMolecule`
 * puts in its `value` — one token for "named nobody — applies to everyone", so a cell drop, an
 * Add-intent button and the inspector's picker all resolve to `StageIntent.perspectives: null`.
 * Never a real perspective system name (those are bare slugs; this one is fenced).
 */
export const UNATTRIBUTED_LANE_KEY = '__unattributed__';

/** The four action shapes the palette's Action tiles drop (`act-attach`, `act-set`, `act-deploy`, `act-factor`). */
export type ArcCanvasDroppableActionType =
  | 'attach_aspect'
  | 'set_aspect_value'
  | 'deploy_approach'
  | 'attach_status_factor';

/** The four action types the inspector can author in full (spec Data Model "Action"); the
 * factor kinds are preserved on round-trip but configured outside the canvas. */
export type ArcCanvasAuthoredActionType = 'deploy_approach' | 'attach_aspect' | 'detach_aspect' | 'set_aspect_value';

/** Every `TargetResolver.kind` the inspector's "Runs on" select can author (spec D8/D39): the
 * two config-free kinds (`self`, `parent`) plus the three that carry their own config block
 * (`descendant`, `context_member`, `created_by_action`) — see `setActionTarget`. */
export type ArcCanvasAuthoredTargetKind = 'self' | 'parent' | 'descendant' | 'context_member' | 'created_by_action';

/** The canonical empty `UnifiedFilters` shape a freshly-seeded `descendant` target starts with —
 * matches `EMPTY_FILTERS` conventions elsewhere in the design system (`useColumnFilters.ts`). */
const EMPTY_TARGET_FILTERS: UnifiedFilters = { propertyFilters: [], perspectiveFilters: [] };

/**
 * Every ArcTemplateDefinition field the canvas does not edit directly (arcSettings,
 * notifications, webhooks, onComplete, etc.) — carried through untouched so a
 * round trip through the canvas never drops something the skill authored.
 */
export type ArcCanvasUnauthoredFields = Omit<
  ArcTemplateDefinition,
  'name' | 'description' | 'version' | 'stages' | 'keyDates'
>;

export interface ArcCanvasModel {
  name: string;
  description?: string;
  version?: string;
  stages: StageDefinition[];
  keyDates: Record<string, KeyDateDefinition>;
  unauthored: ArcCanvasUnauthoredFields;
}

export interface ArcEdge {
  from: string;
  to: string;
  kind: 'manual' | 'auto' | 'calculated';
  label: string;
  /** The `AvailableAction` this edge is drawn from (manual/auto); absent for a calculated edge.
   * Selecting an edge selects the source stage's transition at this action. */
  actionId?: string;
}

/** What a gate check's chip selects when activated — a stage, or one action on a stage
 * (`useArcEditorCanvasState`'s `selectStage` / `selectAction`). */
export type GateCheckSelection =
  | { kind: 'stage'; stageId: string }
  | { kind: 'action'; stageId: string; actionId: string };

export interface GateCheck {
  id: string;
  status: 'open';
  message: string;
  selection: GateCheckSelection;
}

export interface LaneRow {
  laneKey: string;
  label: string;
  stageIds: string[];
}

export interface ArcCanvasSummaryCounts {
  pathsCount: number;
  intentsCount: number;
  actionsCount: number;
  unreachableCount: number;
}

export function definitionToModel(definition: ArcTemplateDefinition): ArcCanvasModel {
  const { name, description, version, stages, keyDates, ...unauthored } = definition;
  return { name, description, version, stages, keyDates, unauthored };
}

export function modelToDefinition(model: ArcCanvasModel): ArcTemplateDefinition {
  return {
    ...model.unauthored,
    name: model.name,
    description: model.description,
    version: model.version,
    stages: model.stages,
    keyDates: model.keyDates,
  };
}

export function deriveEdges(model: ArcCanvasModel): ArcEdge[] {
  const edges: ArcEdge[] = [];
  for (const stage of model.stages) {
    const transition = stage.transition;
    if (!transition) continue;

    if (transition.type === 'manual' || transition.type === 'auto') {
      for (const action of transition.availableActions ?? []) {
        edges.push({
          from: stage.id,
          to: action.nextStageId,
          kind: transition.type,
          label: action.label ?? action.id,
          actionId: action.id,
        });
      }
    } else if (transition.type === 'calculated' && transition.calculatedTarget) {
      edges.push({
        from: stage.id,
        to: transition.calculatedTarget,
        kind: 'calculated',
        label: 'Calculated',
      });
    }
  }
  return edges;
}

/**
 * The primary linear path through the arc: the entry stage (first in `stages`)
 * followed by each stage's first outgoing edge, until a stage repeats or has none.
 */
export function spineOf(model: ArcCanvasModel): string[] {
  const edgesByFrom = new Map<string, ArcEdge[]>();
  for (const edge of deriveEdges(model)) {
    const list = edgesByFrom.get(edge.from) ?? [];
    list.push(edge);
    edgesByFrom.set(edge.from, list);
  }

  const spine: string[] = [];
  const visited = new Set<string>();
  let current: string | undefined = model.stages[0]?.id;
  while (current && !visited.has(current)) {
    spine.push(current);
    visited.add(current);
    current = edgesByFrom.get(current)?.[0]?.to;
  }
  return spine;
}

export function offSpineOf(model: ArcCanvasModel): string[] {
  const spine = new Set(spineOf(model));
  return model.stages.map((stage) => stage.id).filter((id) => !spine.has(id));
}

export function unreachableStages(model: ArcCanvasModel): string[] {
  const targets = new Set(deriveEdges(model).map((edge) => edge.to));
  const entryId = model.stages[0]?.id;
  return model.stages
    .filter((stage) => stage.id !== entryId && !targets.has(stage.id))
    .map((stage) => stage.id);
}

/**
 * `summary` region counts (`arc-app.jsx:342` — "N stages · N paths · N intents · N actions"):
 * paths are transitions (`deriveEdges`), actions are every `onEnter`/`onExit` action across
 * all stages, matching the prototype's `effTrans`/`effActions` tallies.
 */
export function summaryCountsFor(model: ArcCanvasModel): ArcCanvasSummaryCounts {
  const pathsCount = deriveEdges(model).length;
  const intentsCount = model.stages.reduce((sum, stage) => sum + (stage.intents?.length ?? 0), 0);
  const actionsCount = model.stages.reduce(
    (sum, stage) => sum + (stage.onEnter?.length ?? 0) + (stage.onExit?.length ?? 0),
    0
  );
  const unreachableCount = unreachableStages(model).length;

  return { pathsCount, intentsCount, actionsCount, unreachableCount };
}

/** The forward-compat member of `StageActionDefinition` (`{ type: string; [k]: unknown }`) keeps a
 * `type` narrowing from reaching `approach`, so the reference is read the same way
 * `aspectSystemNameOf` reads `aspectSystemName`. */
function approachSystemNameOf(action: StageActionDefinition): string | undefined {
  if (action.type !== 'deploy_approach') return undefined;
  return (action as { approach?: { systemName?: string } }).approach?.systemName;
}

/** Same forward-compat cast as `aspectSystemNameOf`/`approachSystemNameOf` — the fallback
 * member's index signature makes `action.target` type as `unknown` across the union. */
function targetOf(action: StageActionDefinition): TargetResolver {
  return (action as { target: TargetResolver }).target;
}

function aspectSystemNameOf(action: StageActionDefinition): string | undefined {
  if (
    action.type === 'set_aspect_value' ||
    action.type === 'attach_aspect' ||
    action.type === 'detach_aspect'
  ) {
    return (action as { aspectSystemName?: string }).aspectSystemName;
  }
  return undefined;
}

/** Publish-blocking structural defects. Every entry is an unresolved `status: 'open'` check. */
export function structuralChecks(model: ArcCanvasModel): GateCheck[] {
  const checks: GateCheck[] = [];
  const stageIds = new Set(model.stages.map((stage) => stage.id));

  const terminals = model.stages.filter((stage) => stage.isTerminal);
  if (terminals.length === 0) {
    checks.push({
      id: 'no-terminal-stage',
      status: 'open',
      message: 'The arc has no terminal stage.',
      selection: { kind: 'stage', stageId: model.stages[model.stages.length - 1]?.id ?? '' },
    });
  } else if (terminals.length > 1) {
    checks.push({
      id: 'multiple-terminal-stages',
      status: 'open',
      message: `The arc has ${terminals.length} terminal stages; exactly one is required.`,
      selection: { kind: 'stage', stageId: terminals[terminals.length - 1].id },
    });
  }

  for (const stageId of unreachableStages(model)) {
    checks.push({
      id: 'unreachable-stage',
      status: 'open',
      message: `Stage "${stageId}" is unreachable: no transition leads to it.`,
      selection: { kind: 'stage', stageId },
    });
  }

  for (const stage of model.stages) {
    // The stage's own key-date anchor (`startOffsetAnchor` — the card's key-date chip and the
    // stage inspector's "When" field) must name a declared key date, like a transition's.
    if (stage.startOffsetAnchor && !(stage.startOffsetAnchor in model.keyDates)) {
      checks.push({
        id: 'undeclared-key-date-anchor',
        status: 'open',
        message: `Stage "${stage.id}" starts from undeclared key date "${stage.startOffsetAnchor}".`,
        selection: { kind: 'stage', stageId: stage.id },
      });
    }

    const transition = stage.transition;
    if (!transition) continue;

    if (transition.type === 'auto') {
      const targetCount = transition.availableActions?.length ?? 0;
      if (targetCount !== 1) {
        checks.push({
          id:
            targetCount === 0
              ? 'auto-transition-no-target'
              : 'auto-transition-multiple-targets',
          status: 'open',
          message: `Auto transition on stage "${stage.id}" must have exactly one target (has ${targetCount}).`,
          selection: { kind: 'stage', stageId: stage.id },
        });
      }
      if (transition.dateOffsetAnchor && !(transition.dateOffsetAnchor in model.keyDates)) {
        checks.push({
          id: 'undeclared-key-date-anchor',
          status: 'open',
          message: `Stage "${stage.id}" references undeclared key date "${transition.dateOffsetAnchor}".`,
          selection: { kind: 'stage', stageId: stage.id },
        });
      }
    }

    if (transition.type === 'calculated') {
      if (!transition.calculatedTarget) {
        checks.push({
          id: 'calculated-transition-missing-target',
          status: 'open',
          message: `Calculated transition on stage "${stage.id}" has no target.`,
          selection: { kind: 'stage', stageId: stage.id },
        });
      }
      if (!transition.calculatedConditions) {
        checks.push({
          id: 'calculated-transition-missing-condition',
          status: 'open',
          message: `Calculated transition on stage "${stage.id}" has no condition.`,
          selection: { kind: 'stage', stageId: stage.id },
        });
      }
    }
  }

  for (const edge of deriveEdges(model)) {
    if (!stageIds.has(edge.to)) {
      checks.push({
        id: 'dangling-transition-target',
        status: 'open',
        message: `Stage "${edge.from}" transitions to unknown stage "${edge.to}".`,
        selection: { kind: 'stage', stageId: edge.from },
      });
    }
  }

  for (const stage of model.stages) {
    for (const action of [...(stage.onEnter ?? []), ...(stage.onExit ?? [])]) {
      if (
        (action.type === 'set_aspect_value' ||
          action.type === 'attach_aspect' ||
          action.type === 'detach_aspect') &&
        !aspectSystemNameOf(action)
      ) {
        checks.push({
          id: 'empty-aspect-reference',
          status: 'open',
          message: `Action "${action.id}" on stage "${stage.id}" has no aspect reference.`,
          selection: { kind: 'action', stageId: stage.id, actionId: action.id },
        });
      }
      if (action.type === 'deploy_approach' && !approachSystemNameOf(action)) {
        checks.push({
          id: 'empty-approach-reference',
          status: 'open',
          message: `Action "${action.id}" on stage "${stage.id}" has no approach reference.`,
          selection: { kind: 'action', stageId: stage.id, actionId: action.id },
        });
      }
      const target = targetOf(action);
      if (target.kind === 'context_member' && !target.contextSystemName) {
        checks.push({
          id: 'empty-target-reference',
          status: 'open',
          message: `Action "${action.id}" on stage "${stage.id}" has no target context.`,
          selection: { kind: 'action', stageId: stage.id, actionId: action.id },
        });
      }
      if (target.kind === 'created_by_action' && !target.actionId) {
        checks.push({
          id: 'empty-target-reference',
          status: 'open',
          message: `Action "${action.id}" on stage "${stage.id}" has no earlier action to target.`,
          selection: { kind: 'action', stageId: stage.id, actionId: action.id },
        });
      }
    }
  }

  return checks;
}

/**
 * Applies a trigger-kind switch to a transition, clearing every field that belongs to a
 * kind other than the one being switched to. `availableActions` and `allowedTargets` are
 * shared by manual and auto transitions, so a switch between those two carries them over
 * unchanged (`arc-inspector.jsx` `TransitionEditor` kind switch; EAINT-951 SC in
 * arc-editor-canvas-v1.md `transitionKindSwitchClearsForeignFields`).
 */
export function changeTransitionKind(
  transition: TransitionDefinition,
  kind: TransitionDefinition['type']
): TransitionDefinition {
  const next: TransitionDefinition = { ...transition, type: kind };

  if (kind !== 'auto') {
    delete next.anchorSource;
    delete next.dateOffsetAnchor;
    delete next.dateOffset;
  }
  if (kind !== 'calculated') {
    delete next.calculatedConditions;
    delete next.calculatedTarget;
  }
  if (kind !== 'manual') {
    delete next.manualOverrideAllowed;
    delete next.manualOverrideRole;
    delete next.requireComment;
  }

  return next;
}

/**
 * Adds a manual-exit target to `stageId` — the flow view's "Transition" button
 * (`addTransition`, `arc-app.jsx:217`): a new `AvailableAction` pointing at the first other
 * stage this stage doesn't already target (wrapping to the next stage in declaration order
 * when every other stage is already a target), switching the stage's transition to `manual`
 * first via `changeTransitionKind` when it holds a different kind. No-op when `stageId` is
 * unknown, the model has fewer than two stages, or every other stage is already targeted.
 */
export function addTransitionTarget(model: ArcCanvasModel, stageId: string): ArcCanvasModel {
  const index = model.stages.findIndex((stage) => stage.id === stageId);
  if (index === -1 || model.stages.length < 2) return model;

  const stage = model.stages[index];
  const existingTargets = new Set(
    (stage.transition?.availableActions ?? []).map((action) => action.nextStageId)
  );
  const target =
    model.stages.find((other) => other.id !== stageId && !existingTargets.has(other.id)) ?? null;
  if (!target) return model;

  const transition = changeTransitionKind(stage.transition ?? { type: 'manual' }, 'manual');
  const availableActions = [
    ...(transition.availableActions ?? []),
    { id: `t${Date.now()}`, label: 'new path', nextStageId: target.id },
  ];

  const stages = [...model.stages];
  stages[index] = { ...stage, transition: withAllowedTargets({ ...transition, availableActions }) };
  return { ...model, stages };
}

/**
 * Re-derives `allowedTargets` from the fields that carry the transition's targets (spec § Model
 * mapping): manual/auto — the `nextStageId`s of `availableActions`; calculated — the
 * `calculatedTarget`. The whitelist is never edited directly, so it cannot disagree with the
 * targets the editor shows. Empty targets are dropped (a carrier created before its target is
 * picked). Returns `transition` itself when the list is already right.
 */
export function withAllowedTargets(transition: TransitionDefinition): TransitionDefinition {
  const targets =
    transition.type === 'calculated'
      ? transition.calculatedTarget
        ? [transition.calculatedTarget]
        : []
      : Array.from(
          new Set((transition.availableActions ?? []).map((action) => action.nextStageId).filter(Boolean))
        );
  const current = transition.allowedTargets ?? [];
  if (current.length === targets.length && current.every((id, i) => id === targets[i])) return transition;
  return { ...transition, allowedTargets: targets };
}

/** Fields `ArcTransitionInspectorMolecule` edits on a stage's exit. `toStageId` and `label`
 * address one `AvailableAction` (the edge that was selected, or the stage's first exit) for
 * manual/auto transitions and `calculatedTarget` for calculated ones; the remaining fields map
 * 1:1 onto `TransitionDefinition`. */
export interface ArcTransitionUpdates {
  kind?: TransitionDefinition['type'];
  toStageId?: string;
  label?: string;
  anchorSource?: TransitionDefinition['anchorSource'];
  dateOffsetAnchor?: string;
  dateOffset?: string;
  calculatedConditions?: TransitionDefinition['calculatedConditions'];
  requireComment?: boolean;
}

function transitionActionIndex(transition: TransitionDefinition, actionId: string | null): number {
  const actions = transition.availableActions ?? [];
  if (actionId === null) return actions.length ? 0 : -1;
  return actions.findIndex((action) => action.id === actionId);
}

/**
 * Applies `updates` to `stageId`'s exit transition. `actionId` names the `AvailableAction` an
 * edge click selected (`ArcEdge.actionId`); `null` addresses the stage's first exit, and a
 * stage with no exit yet gets a carrier action the moment a target or label is typed (its
 * empty `nextStageId` surfaces on the publish gate until a target is picked — `addAction`'s
 * empty-reference precedent). A `kind` change goes through `changeTransitionKind`, so fields
 * that don't belong to the new kind are cleared; `allowedTargets` is re-derived afterwards.
 * Returns the model unchanged when `stageId` doesn't resolve or nothing actually changed.
 */
export function updateTransition(
  model: ArcCanvasModel,
  stageId: string,
  actionId: string | null,
  updates: ArcTransitionUpdates
): ArcCanvasModel {
  const index = model.stages.findIndex((stage) => stage.id === stageId);
  if (index === -1) return model;

  const stage = model.stages[index];
  const current = stage.transition ?? { type: 'manual' };
  let next: TransitionDefinition =
    updates.kind && updates.kind !== current.type ? changeTransitionKind(current, updates.kind) : { ...current };

  const { anchorSource, dateOffsetAnchor, dateOffset, calculatedConditions, requireComment, toStageId, label } = updates;
  if (anchorSource !== undefined) next.anchorSource = anchorSource;
  if (dateOffsetAnchor !== undefined) next.dateOffsetAnchor = dateOffsetAnchor;
  if (dateOffset !== undefined) next.dateOffset = dateOffset;
  if (calculatedConditions !== undefined) next.calculatedConditions = calculatedConditions;
  if (requireComment !== undefined) next.requireComment = requireComment;

  const editsAction = toStageId !== undefined || label !== undefined;
  if (next.type === 'calculated' && toStageId !== undefined) next.calculatedTarget = toStageId || undefined;
  if (editsAction && !(next.type === 'calculated' && label === undefined)) {
    const actions = [...(next.availableActions ?? [])];
    let actionIndex = transitionActionIndex(next, actionId);
    if (actionIndex === -1) {
      actions.push({ id: `t${Date.now()}`, label: '', nextStageId: '' });
      actionIndex = actions.length - 1;
    }
    const action = { ...actions[actionIndex] };
    if (label !== undefined) action.label = label;
    if (toStageId !== undefined && next.type !== 'calculated') action.nextStageId = toStageId;
    actions[actionIndex] = action;
    next.availableActions = actions;
  }
  next = withAllowedTargets(next);

  if (transitionsEqual(current, next)) return model;
  const stages = [...model.stages];
  stages[index] = { ...stage, transition: next };
  return { ...model, stages };
}

function transitionsEqual(a: TransitionDefinition, b: TransitionDefinition): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * The `AvailableAction` a transition selection addresses when nothing more specific was clicked:
 * the stage's first manual/auto exit, or `null` when the exit is calculated (no action carries it)
 * or the stage has no exit yet. `selectTransition` resolves the stage card's transition button
 * through this so the inspector, the flow view's highlighted edge and Remove all name one exit.
 */
export function firstExitActionIdOf(transition: TransitionDefinition | undefined): string | null {
  if (!transition || transition.type === 'calculated') return null;
  return transition.availableActions?.[0]?.id ?? null;
}

/**
 * Whether removing `actionId` from `transition` leaves the stage other exits — true only when the
 * id names one of several manual/auto `AvailableAction`s. `removeTransition` branches on this and
 * the inspector's remove confirm reads it to state the right effect (one path goes vs. the stage
 * has no way forward), so the copy and the mutation cannot disagree.
 */
export function transitionRemoveKeepsOtherExits(
  transition: TransitionDefinition | undefined,
  actionId: string | null
): boolean {
  if (!transition || transition.type === 'calculated' || actionId === null) return false;
  const actions = transition.availableActions ?? [];
  return actions.length > 1 && actions.some((action) => action.id === actionId);
}

/**
 * Removes an exit from `stageId` — the transition inspector's Remove, after its confirm. When
 * `transitionRemoveKeepsOtherExits` holds only that `AvailableAction` goes; otherwise the whole
 * transition resets to `{ type: 'manual' }` with no targets, which the engine reads as terminal
 * behaviour (the confirm copy: "no way forward until you add another"). Returns the model
 * unchanged when `stageId` doesn't resolve.
 */
export function removeTransition(model: ArcCanvasModel, stageId: string, actionId: string | null): ArcCanvasModel {
  const index = model.stages.findIndex((stage) => stage.id === stageId);
  if (index === -1) return model;

  const stage = model.stages[index];
  const current = stage.transition ?? { type: 'manual' };
  const actions = current.availableActions ?? [];
  const transition: TransitionDefinition = transitionRemoveKeepsOtherExits(current, actionId)
    ? withAllowedTargets({ ...current, availableActions: actions.filter((action) => action.id !== actionId) })
    : { type: 'manual' };

  const stages = [...model.stages];
  stages[index] = { ...stage, transition };
  return { ...model, stages };
}

/**
 * Renames a stage in place: `fallbackName` is the editable display text (the "Investigating"
 * card-header value the stage card's `onRename` carries — `ArcStageCardMolecule`); the stage's
 * `id` and `localizationKey` are identifiers other parts of the definition (transitions, key
 * dates, aspect requirements) reference and are never touched by a rename (EAINT-951 "stage
 * rename in place"). Returns the model unchanged (same reference) when `stageId` doesn't match
 * any stage, so a caller can call this unconditionally without a existence check.
 */
export function renameStage(
  model: ArcCanvasModel,
  stageId: string,
  fallbackName: string
): ArcCanvasModel {
  const index = model.stages.findIndex((stage) => stage.id === stageId);
  if (index === -1) return model;

  const stages = [...model.stages];
  stages[index] = { ...stages[index], fallbackName };
  return { ...model, stages };
}

/** Derives a stage id from a display name under the platform's one `system_name` convention
 * (`slugifyAspectName`: lowercase, underscore-joined, non-alphanumerics stripped). */
export function slugStageId(name: string): string {
  return slugifyAspectName(name);
}

/** Appends `_2`, `_3`, ... to `base` until it no longer collides with a stage id already in `model`. */
function uniqueStageId(model: ArcCanvasModel, base: string): string {
  const taken = new Set(model.stages.map((stage) => stage.id));
  const root = base || 'stage';
  if (!taken.has(root)) return root;

  let suffix = 2;
  let candidate = `${root}_${suffix}`;
  while (taken.has(candidate)) {
    suffix += 1;
    candidate = `${root}_${suffix}`;
  }
  return candidate;
}

/** One of the shapes offered when a template has no stages yet (`AC_STARTERS`,
 * `arc-app.jsx:33-36`). The prototype's kind/archetype step lives in the gallery's New Arc dialog
 * (`StudioTypeGalleryPage`), which applies a starter itself when the chosen archetype requires a
 * stage; the canvas offers the same starters to a template that still has none. */
export interface ArcCanvasStarter {
  id: string;
  stageNames: string[];
}

export const ARC_CANVAS_STARTERS: ArcCanvasStarter[] = [
  { id: 'two', stageNames: ['Open', 'Closed'] },
  { id: 'three', stageNames: ['To do', 'Doing', 'Done'] },
];

/**
 * Replaces the model's stages with one of `ARC_CANVAS_STARTERS`'s shapes (`addStarter`,
 * `arc-app.jsx:190-196`): each stage gets a unique id and a manual transition with one
 * `availableActions` entry pointing to the next stage; the last stage is terminal with no
 * outbound target. Returns the model unchanged when `starterId` doesn't match a known starter.
 */
export function applyStarter(model: ArcCanvasModel, starterId: string): ArcCanvasModel {
  const starter = ARC_CANVAS_STARTERS.find((candidate) => candidate.id === starterId);
  if (!starter) return model;

  const ids = starter.stageNames.map((name) => slugStageId(name));
  const stages: StageDefinition[] = starter.stageNames.map((fallbackName, index) => {
    const id = ids[index];
    const isLast = index === starter.stageNames.length - 1;
    return {
      id,
      type: 'manual',
      assignedTo: { type: 'perspective', value: 'responsible' },
      isTerminal: isLast,
      transition: {
        type: 'manual',
        availableActions: isLast
          ? []
          : [{ id: `t${index}${Date.now()}`, label: 'move on', nextStageId: ids[index + 1] }],
      },
      fallbackName,
      localizationKey: id,
    };
  });

  return { ...model, stages };
}

/**
 * Adds a new, unwired manual stage (`arc-app.jsx:177-186`'s `addStage`): no transitions in or
 * out, no intents, no actions — the palette's "Add a stage" affordance, closed by the author
 * naming it via the same in-place rename `renameStage` already supports. Placed immediately
 * after `afterStageId`, or at the end when `afterStageId` is `null`/unknown. `terminal` marks
 * the stage `isTerminal` (the palette's "Terminal stage" tile); a plain stage leaves the flag
 * unset, which the checks and the flow view read as non-terminal.
 */
export function addStage(
  model: ArcCanvasModel,
  afterStageId: string | null,
  fallbackName: string,
  options: { terminal?: boolean } = {}
): ArcCanvasModel {
  const id = uniqueStageId(model, slugStageId(fallbackName));
  const newStage: StageDefinition = {
    id,
    type: 'manual',
    assignedTo: { type: 'perspective', value: 'responsible' },
    transition: { type: 'manual', availableActions: [] },
    fallbackName,
    localizationKey: id,
    ...(options.terminal ? { isTerminal: true } : {}),
  };

  const afterIndex = afterStageId ? model.stages.findIndex((stage) => stage.id === afterStageId) : -1;
  const insertAt = afterIndex === -1 ? model.stages.length : afterIndex + 1;

  const stages = [...model.stages];
  stages.splice(insertAt, 0, newStage);
  return { ...model, stages };
}

/** Removes `stageId` from the model (the inspector rail's Remove action on the stage editor).
 * Leaves every other stage's `transition`/`allowedFrom` untouched — a reference to the removed
 * id becomes a `dangling-transition-target` `structuralChecks` finding, the same way a freshly
 * `addStage`d stage is left unwired until an author configures it. Returns the model unchanged
 * when `stageId` doesn't match any stage. */
export function removeStage(model: ArcCanvasModel, stageId: string): ArcCanvasModel {
  const stages = model.stages.filter((stage) => stage.id !== stageId);
  if (stages.length === model.stages.length) return model;
  return { ...model, stages };
}

/** The engine reads a stage or transition offset as whole days only (`+1d`, `-3d`, `0d`); anything
 * else (`4h`, `1.5d`, `2`) is silently read as `0d`. An empty offset is not an offset and
 * passes — the anchor alone dates the stage. */
export const WHOLE_DAY_OFFSET_PATTERN = /^[+-]?\d+d$/;

export function isWholeDayOffset(offset: string | undefined): boolean {
  return !offset || WHOLE_DAY_OFFSET_PATTERN.test(offset.trim());
}

/** Fields `ArcStageInspectorMolecule` can apply outside of name (`renameStage` owns that field —
 * several other parts of the definition key off the stage's `id`, never its display name, so
 * renaming is kept isolated). `assignedTo` replaces the perspective/user/group `value`, preserving
 * the existing `type`. */
export type ArcStageUpdates = Partial<
  Pick<StageDefinition, 'startOffset' | 'startOffsetAnchor' | 'isTerminal'>
> & { assignedTo?: string };

/** Applies `updates` to `stageId` (the inspector rail's When/Rests/Owns sections). Returns the
 * model unchanged when `stageId` doesn't resolve. */
export function updateStage(
  model: ArcCanvasModel,
  stageId: string,
  updates: ArcStageUpdates
): ArcCanvasModel {
  const index = model.stages.findIndex((stage) => stage.id === stageId);
  if (index === -1) return model;

  const { assignedTo, ...fieldUpdates } = updates;
  const stage = model.stages[index];
  const updatedStage: StageDefinition = {
    ...stage,
    ...fieldUpdates,
    ...(assignedTo !== undefined ? { assignedTo: { ...stage.assignedTo, value: assignedTo } } : {}),
  };

  const stages = [...model.stages];
  stages[index] = updatedStage;
  return { ...model, stages };
}

/** Appends a blank requirement to `stageId`'s `requires` (the inspector rail's "Require an
 * aspect" affordance) — an empty `aspectSystemName` is deliberate, matching `addAction`'s
 * empty-reference precedent: it surfaces on the publish gate (`empty-aspect-reference`-style)
 * until an author picks an aspect. Returns the model unchanged when `stageId` doesn't resolve. */
export function addStageRequirement(model: ArcCanvasModel, stageId: string): ArcCanvasModel {
  const index = model.stages.findIndex((stage) => stage.id === stageId);
  if (index === -1) return model;

  const stage = model.stages[index];
  const requires = [...(stage.requires ?? []), { aspectSystemName: '', prompt: '' }];
  const stages = [...model.stages];
  stages[index] = { ...stage, requires };
  return { ...model, stages };
}

/** Applies `updates` to the requirement at `requirementIndex` on `stageId`'s `requires`. Returns
 * the model unchanged when `stageId`/`requirementIndex` don't resolve. */
export function updateStageRequirement(
  model: ArcCanvasModel,
  stageId: string,
  requirementIndex: number,
  updates: Partial<AspectRequirement>
): ArcCanvasModel {
  const index = model.stages.findIndex((stage) => stage.id === stageId);
  if (index === -1) return model;

  const stage = model.stages[index];
  const existing = stage.requires ?? [];
  if (requirementIndex < 0 || requirementIndex >= existing.length) return model;

  const requires = [...existing];
  requires[requirementIndex] = { ...requires[requirementIndex], ...updates };
  const stages = [...model.stages];
  stages[index] = { ...stage, requires };
  return { ...model, stages };
}

/** Removes the requirement at `requirementIndex` from `stageId`'s `requires`. Returns the model
 * unchanged when `stageId`/`requirementIndex` don't resolve. */
export function removeStageRequirement(
  model: ArcCanvasModel,
  stageId: string,
  requirementIndex: number
): ArcCanvasModel {
  const index = model.stages.findIndex((stage) => stage.id === stageId);
  if (index === -1) return model;

  const stage = model.stages[index];
  const existing = stage.requires ?? [];
  if (requirementIndex < 0 || requirementIndex >= existing.length) return model;

  const requires = existing.filter((_, i) => i !== requirementIndex);
  const stages = [...model.stages];
  stages[index] = { ...stage, requires };
  return { ...model, stages };
}

/** Appends `_2`, `_3`, ... to `base` until it no longer collides with an intent id already on `stage`. */
function uniqueIntentId(stage: StageDefinition, base: string): string {
  const taken = new Set((stage.intents ?? []).map((intent) => intent.id));
  if (!taken.has(base)) return base;

  let suffix = 2;
  let candidate = `${base}_${suffix}`;
  while (taken.has(candidate)) {
    suffix += 1;
    candidate = `${base}_${suffix}`;
  }
  return candidate;
}

/** Appends `_2`, `_3`, ... to `base` until it no longer collides with an action id already on `stage`. */
function uniqueActionId(stage: StageDefinition, base: string): string {
  const taken = new Set([...(stage.onEnter ?? []), ...(stage.onExit ?? [])].map((action) => action.id));
  if (!taken.has(base)) return base;

  let suffix = 2;
  let candidate = `${base}_${suffix}`;
  while (taken.has(candidate)) {
    suffix += 1;
    candidate = `${base}_${suffix}`;
  }
  return candidate;
}

/**
 * Adds a new, unfulfilled intent to `stageId` (the palette's Intent tiles dropped into a lane
 * cell — `arc-app.jsx:197-201`'s `addIntent`): no `fulfillmentCondition`/`prerequisites`, medium
 * priority, attributed to `laneKey` (or unattributed when `laneKey` is `UNATTRIBUTED_LANE_KEY`), closed
 * by the author filling in `ArcIntentInspectorMolecule`. Returns the model unchanged when
 * `stageId` doesn't match any stage.
 */
export function addIntent(
  model: ArcCanvasModel,
  stageId: string,
  laneKey: string,
  fallbackName: string
): ArcCanvasModel {
  const index = model.stages.findIndex((stage) => stage.id === stageId);
  if (index === -1) return model;

  const stage = model.stages[index];
  const newIntent: StageIntent = {
    id: uniqueIntentId(stage, 'intent'),
    name: fallbackName,
    priority: 'medium',
    perspectives: laneKey === UNATTRIBUTED_LANE_KEY ? null : [laneKey],
  };

  const stages = [...model.stages];
  stages[index] = { ...stage, intents: [...(stage.intents ?? []), newIntent] };
  return { ...model, stages };
}

/** Fields `ArcIntentInspectorMolecule` can apply to an existing intent; `stageId` present and
 * different from the intent's current stage moves it there. */
export type ArcIntentUpdates = Partial<
  Pick<StageIntent, 'name' | 'priority' | 'perspectives' | 'fulfillmentCondition' | 'prerequisites'>
> & { stageId?: string };

/**
 * Applies `updates` to `intentId` on `stageId` (the inspector rail's Apply action). A `stageId`
 * update that names a different stage moves the intent there, preserving every other field.
 * Returns the model unchanged when `stageId`/`intentId`/the target `stageId` don't resolve.
 */
export function updateIntent(
  model: ArcCanvasModel,
  stageId: string,
  intentId: string,
  updates: ArcIntentUpdates
): ArcCanvasModel {
  const sourceIndex = model.stages.findIndex((stage) => stage.id === stageId);
  if (sourceIndex === -1) return model;

  const sourceStage = model.stages[sourceIndex];
  const intentIndex = (sourceStage.intents ?? []).findIndex((intent) => intent.id === intentId);
  if (intentIndex === -1) return model;

  const { stageId: targetStageId, ...fieldUpdates } = updates;
  const updatedIntent: StageIntent = { ...sourceStage.intents![intentIndex], ...fieldUpdates };

  if (!targetStageId || targetStageId === stageId) {
    const intents = [...sourceStage.intents!];
    intents[intentIndex] = updatedIntent;
    const stages = [...model.stages];
    stages[sourceIndex] = { ...sourceStage, intents };
    return { ...model, stages };
  }

  const targetIndex = model.stages.findIndex((stage) => stage.id === targetStageId);
  if (targetIndex === -1) return model;

  const stages = [...model.stages];
  stages[sourceIndex] = {
    ...sourceStage,
    intents: sourceStage.intents!.filter((intent) => intent.id !== intentId),
  };
  const targetStage = stages[targetIndex];
  stages[targetIndex] = { ...targetStage, intents: [...(targetStage.intents ?? []), updatedIntent] };
  return { ...model, stages };
}

/** Removes `intentId` from `stageId`'s intents (the inspector rail's Remove action). Returns the
 * model unchanged when `stageId`/`intentId` don't resolve. */
export function removeIntent(model: ArcCanvasModel, stageId: string, intentId: string): ArcCanvasModel {
  const index = model.stages.findIndex((stage) => stage.id === stageId);
  if (index === -1) return model;

  const stage = model.stages[index];
  const intents = (stage.intents ?? []).filter((intent) => intent.id !== intentId);
  if (intents.length === (stage.intents ?? []).length) return model;

  const stages = [...model.stages];
  stages[index] = { ...stage, intents };
  return { ...model, stages };
}

/**
 * Adds a new, unconfigured action to `stageId`'s `onEnter` list (the palette's Action tiles
 * dropped into a lane cell — `arc-app.jsx:204-208`'s `addAction`): a `self` target, `confirm`
 * tier, and empty per-type reference fields (`aspectSystemName: ''`, an empty `approach`
 * reference, or a bare factor shell) an author fills in via `ArcActionInspectorMolecule`. The
 * blank `aspectSystemName` is deliberate — `structuralChecks`' `empty-aspect-reference` check
 * already flags exactly this state, so a freshly dropped action surfaces on the publish gate
 * until configured. Returns the model unchanged when `stageId` doesn't match any stage.
 */
export function addAction(
  model: ArcCanvasModel,
  stageId: string,
  actionType: ArcCanvasDroppableActionType
): ArcCanvasModel {
  const index = model.stages.findIndex((stage) => stage.id === stageId);
  if (index === -1) return model;

  const stage = model.stages[index];
  const id = uniqueActionId(stage, actionType);
  const base = { id, target: { kind: 'self' as const }, tier: 'confirm' as const };

  let action: StageActionDefinition;
  switch (actionType) {
    case 'attach_aspect':
      action = { ...base, type: 'attach_aspect', aspectSystemName: '' };
      break;
    case 'set_aspect_value':
      action = { ...base, type: 'set_aspect_value', aspectSystemName: '', value: '' };
      break;
    case 'deploy_approach':
      action = { ...base, type: 'deploy_approach', approach: { source: 'tenant', systemName: '', version: 'latest' } };
      break;
    case 'attach_status_factor':
      action = {
        ...base,
        type: 'attach_status_factor',
        factor: {
          id: `${id}_factor`,
          name: { default: '' },
          factorType: 'manual',
          weight: 1,
          enabled: true,
          autoDeployOnApproachDeploy: true,
        },
      };
      break;
  }

  const stages = [...model.stages];
  stages[index] = { ...stage, onEnter: [...(stage.onEnter ?? []), action] };
  return { ...model, stages };
}

/** Fields `ArcActionInspectorMolecule` can apply to an existing action: `tier` (the
 * `ChoiceCardGroupAtom` ladder), `hook` (moves the action between `onEnter`/`onExit`), `type`
 * (re-shapes the action across the four authored kinds, see `retypeAction`), `reference` (the
 * aspect or approach the resulting type names), `targetKind` (switches the "Runs on" resolver,
 * seeding that kind's defaults via `setActionTarget`), the per-kind target config fields
 * (`targetFilters`/`targetSelection`/`targetMaxDepth` for `descendant`, `targetContextSystemName`
 * for `context_member`, `targetActionId` for `created_by_action`) and `stageId` (moves the
 * action to another stage's same hook). */
export type ArcActionUpdates = {
  tier?: AutomationTier;
  hook?: 'onEnter' | 'onExit';
  type?: ArcCanvasAuthoredActionType;
  /** `aspectSystemName` for the aspect kinds, `approach.systemName` for `deploy_approach`. */
  reference?: string;
  targetKind?: ArcCanvasAuthoredTargetKind;
  /** `descendant` only. */
  targetFilters?: UnifiedFilters;
  /** `descendant` and `context_member` only. */
  targetSelection?: 'first' | 'all';
  /** `descendant` only; integer ≥ 1. Pass `undefined` explicitly to clear it. */
  targetMaxDepth?: number;
  /** `context_member` only. */
  targetContextSystemName?: string;
  /** `created_by_action` only. */
  targetActionId?: string;
  stageId?: string;
};

/**
 * Seeds `kind`'s default target shape on `action` (spec D8/D39's `setActionTarget`): `self`/
 * `parent` carry no config; `descendant` starts with an empty `UnifiedFilters` and `first`
 * selection (no `maxDepth`); `context_member` starts with an empty context and `first`
 * selection; `created_by_action` starts with an empty `actionId`. Always replaces the target
 * wholesale — switching kind drops every field the previous kind carried.
 */
export function setActionTarget(action: StageActionDefinition, kind: ArcCanvasAuthoredTargetKind): StageActionDefinition {
  let target: TargetResolver;
  switch (kind) {
    case 'descendant':
      target = { kind, filters: EMPTY_TARGET_FILTERS, selection: 'first' };
      break;
    case 'context_member':
      target = { kind, contextSystemName: '', selection: 'first' };
      break;
    case 'created_by_action':
      target = { kind, actionId: '' };
      break;
    default:
      target = { kind };
  }
  return { ...action, target };
}

/** Applies the per-kind target config fields of `updates` to `target`, leaving fields the
 * target's kind doesn't own untouched (`updateAction` calls this only when `updates` carries at
 * least one of them, so a plain field-value update never has to specify `targetKind` too). */
function withTargetFieldUpdates(target: TargetResolver, updates: ArcActionUpdates): TargetResolver {
  switch (target.kind) {
    case 'descendant': {
      const next = { ...target };
      if (updates.targetFilters !== undefined) next.filters = updates.targetFilters;
      if (updates.targetSelection !== undefined) next.selection = updates.targetSelection;
      if ('targetMaxDepth' in updates) {
        if (updates.targetMaxDepth === undefined) delete next.maxDepth;
        else next.maxDepth = updates.targetMaxDepth;
      }
      return next;
    }
    case 'context_member': {
      const next = { ...target };
      if (updates.targetContextSystemName !== undefined) next.contextSystemName = updates.targetContextSystemName;
      if (updates.targetSelection !== undefined) next.selection = updates.targetSelection;
      return next;
    }
    case 'created_by_action': {
      const next = { ...target };
      if (updates.targetActionId !== undefined) next.actionId = updates.targetActionId;
      return next;
    }
    default:
      return target;
  }
}

/** Returns the ids of `hook`'s actions on `stage` that appear before `actionId` — the
 * `created_by_action` resolver may only reference an action that has already run earlier in the
 * same hook (an action cannot target the output of one that hasn't executed yet). Returns `[]`
 * when `actionId` isn't found in `hook`. */
export function earlierActionsInHook(
  stage: StageDefinition,
  hook: 'onEnter' | 'onExit',
  actionId: string
): StageActionDefinition[] {
  const list = stage[hook] ?? [];
  const index = list.findIndex((action) => action.id === actionId);
  if (index === -1) return [];
  return list.slice(0, index);
}

/**
 * Re-shapes `action` as `type`, keeping every `BaseStageAction` field (id, label, dependsOn,
 * idempotency, condition, target, tier) and carrying the aspect reference across when both the
 * old and new kinds name one. Switching to `deploy_approach` starts with an empty tenant
 * reference; switching to `set_aspect_value` starts with an empty value. Returns `action`
 * itself when the type does not change.
 */
export function retypeAction(action: StageActionDefinition, type: ArcCanvasAuthoredActionType): StageActionDefinition {
  if (action.type === type) return action;
  const { id, label, dependsOn, idempotency, condition, target, tier } = action as BaseStageAction;
  const base: BaseStageAction = {
    id,
    target,
    ...(label !== undefined ? { label } : {}),
    ...(dependsOn !== undefined ? { dependsOn } : {}),
    ...(idempotency !== undefined ? { idempotency } : {}),
    ...(condition !== undefined ? { condition } : {}),
    ...(tier !== undefined ? { tier } : {}),
  };
  const aspectSystemName = aspectSystemNameOf(action) ?? '';
  switch (type) {
    case 'attach_aspect':
      return { ...base, type, aspectSystemName };
    case 'detach_aspect':
      return { ...base, type, aspectSystemName };
    case 'set_aspect_value':
      return { ...base, type, aspectSystemName, value: '' };
    case 'deploy_approach':
      return { ...base, type, approach: { source: 'tenant', systemName: '', version: 'latest' } };
  }
}

/** Writes `reference` into whichever field the action's type reads it from; unchanged for the
 * factor kinds, which have no single reference. */
function withActionReference(action: StageActionDefinition, reference: string): StageActionDefinition {
  switch (action.type) {
    case 'attach_aspect':
    case 'detach_aspect':
    case 'set_aspect_value':
      return { ...action, aspectSystemName: reference };
    case 'deploy_approach': {
      const approach = (action as { approach?: Record<string, unknown> }).approach ?? {
        source: 'tenant',
        version: 'latest',
      };
      return { ...action, approach: { ...approach, systemName: reference } } as StageActionDefinition;
    }
    default:
      return action;
  }
}

/**
 * Applies `updates` to `actionId` on `stageId` (the inspector rail's Apply action). Actions live
 * in `stage.onEnter`/`stage.onExit`, not a single array like intents, so a `hook` update that
 * names the other list moves the action there, preserving every other field. Returns the model
 * unchanged when `stageId`/`actionId` don't resolve.
 */
export function updateAction(
  model: ArcCanvasModel,
  stageId: string,
  actionId: string,
  updates: ArcActionUpdates
): ArcCanvasModel {
  const stageIndex = model.stages.findIndex((stage) => stage.id === stageId);
  if (stageIndex === -1) return model;

  const stage = model.stages[stageIndex];
  const enterIndex = (stage.onEnter ?? []).findIndex((action) => action.id === actionId);
  const sourceHook: 'onEnter' | 'onExit' = enterIndex !== -1 ? 'onEnter' : 'onExit';
  const sourceIndex =
    enterIndex !== -1 ? enterIndex : (stage.onExit ?? []).findIndex((action) => action.id === actionId);
  if (sourceIndex === -1) return model;

  const sourceList = stage[sourceHook] ?? [];
  const {
    hook,
    type,
    reference,
    targetKind,
    targetFilters,
    targetSelection,
    targetMaxDepth,
    targetContextSystemName,
    targetActionId,
    stageId: targetStageId,
    ...fieldUpdates
  } = updates;
  let updatedAction: StageActionDefinition = { ...sourceList[sourceIndex], ...fieldUpdates };
  if (type) updatedAction = retypeAction(updatedAction, type);
  if (reference !== undefined) updatedAction = withActionReference(updatedAction, reference);
  if (targetKind && targetKind !== targetOf(updatedAction).kind) {
    updatedAction = setActionTarget(updatedAction, targetKind);
  }
  const hasTargetFieldUpdate =
    targetFilters !== undefined ||
    targetSelection !== undefined ||
    'targetMaxDepth' in updates ||
    targetContextSystemName !== undefined ||
    targetActionId !== undefined;
  if (hasTargetFieldUpdate) {
    updatedAction = { ...updatedAction, target: withTargetFieldUpdates(targetOf(updatedAction), updates) };
  }

  const targetHook = hook ?? sourceHook;
  const targetStageIndex =
    targetStageId === undefined ? stageIndex : model.stages.findIndex((candidate) => candidate.id === targetStageId);
  if (targetStageIndex === -1) return model;

  if (targetHook === sourceHook && targetStageIndex === stageIndex) {
    const list = [...sourceList];
    list[sourceIndex] = updatedAction;
    const stages = [...model.stages];
    stages[stageIndex] = { ...stage, [sourceHook]: list };
    return { ...model, stages };
  }

  const stages = [...model.stages];
  stages[stageIndex] = {
    ...stage,
    [sourceHook]: sourceList.filter((action) => action.id !== actionId),
  };
  const targetStage = stages[targetStageIndex];
  stages[targetStageIndex] = {
    ...targetStage,
    [targetHook]: [...(targetStage[targetHook] ?? []), updatedAction],
  };
  return { ...model, stages };
}

/** Removes `actionId` from `stageId`'s `onEnter`/`onExit` (the inspector rail's Remove action).
 * Returns the model unchanged when `stageId`/`actionId` don't resolve. */
export function removeAction(model: ArcCanvasModel, stageId: string, actionId: string): ArcCanvasModel {
  const stageIndex = model.stages.findIndex((stage) => stage.id === stageId);
  if (stageIndex === -1) return model;

  const stage = model.stages[stageIndex];
  const onEnter = (stage.onEnter ?? []).filter((action) => action.id !== actionId);
  const onExit = (stage.onExit ?? []).filter((action) => action.id !== actionId);
  if (onEnter.length === (stage.onEnter ?? []).length && onExit.length === (stage.onExit ?? []).length) {
    return model;
  }

  const stages = [...model.stages];
  stages[stageIndex] = { ...stage, onEnter, onExit };
  return { ...model, stages };
}

/**
 * Groups stages into lane rows by their `assignedTo.value`, in spine order, for the
 * ArcLaneGridMolecule's stage × lane matrix. `schemas` resolves a display label for a
 * lane whose key matches a schema's `system_name`; falls back to the raw key otherwise.
 */
export function laneRowsFor(model: ArcCanvasModel, schemas: Schema[] = []): LaneRow[] {
  const order = [...spineOf(model), ...offSpineOf(model)];
  const lanes = new Map<string, LaneRow>();

  for (const stageId of order) {
    const stage = model.stages.find((s) => s.id === stageId);
    if (!stage) continue;

    const laneKey = stage.assignedTo?.value ?? 'unassigned';
    let lane = lanes.get(laneKey);
    if (!lane) {
      const schemaMatch = schemas.find((schema) => schema.system_name === laneKey);
      lane = { laneKey, label: schemaMatch?.name ?? laneKey, stageIds: [] };
      lanes.set(laneKey, lane);
    }
    lane.stageIds.push(stageId);
  }

  return Array.from(lanes.values());
}

/**
 * The intents on `stage` attributed to `laneKey` (`ArcLaneGridMolecule`'s per-lane cells) — or,
 * when `laneKey` is `null`, the unattributed intents (`StageIntent.perspectives` null/absent/empty,
 * per its own doc comment). A `perspectives` array never carries a sentinel "unattributed" value
 * (unlike the prototype's demo data, which uses `"any"` for convenience) — absence IS the signal.
 */
export function intentsInLane(stage: StageDefinition, laneKey: string | null): StageIntent[] {
  const intents = stage.intents ?? [];
  if (laneKey === null) {
    return intents.filter((intent) => !intent.perspectives || intent.perspectives.length === 0);
  }
  return intents.filter((intent) => !!intent.perspectives?.includes(laneKey));
}

/**
 * Every perspective key named by at least one intent's `perspectives`, across every stage, in
 * first-seen order — the `ArcLaneGridMolecule` `lanes` this canvas can populate from the model
 * alone, with no schema catalog available. Does not include the unattributed lane (rendered
 * separately, structurally) or any perspective a schema declares but no intent currently names
 * (that union is `resolveLanesForModel`, below).
 */
export function distinctIntentLanes(model: ArcCanvasModel): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const stage of model.stages) {
    for (const intent of stage.intents ?? []) {
      for (const laneKey of intent.perspectives ?? []) {
        if (!seen.has(laneKey)) {
          seen.add(laneKey);
          order.push(laneKey);
        }
      }
    }
  }
  return order;
}

/** Total intent count per perspective lane, across every stage — `ArcLaneLabelMolecule`'s `count`. */
export function intentCountByLane(model: ArcCanvasModel): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const stage of model.stages) {
    for (const intent of stage.intents ?? []) {
      for (const laneKey of intent.perspectives ?? []) {
        counts[laneKey] = (counts[laneKey] ?? 0) + 1;
      }
    }
  }
  return counts;
}

export interface ArcLaneDefinition {
  laneKey: string;
  /** True when `laneKey` is not a perspective of `center_baseschema` nor of every schema the
   * applicability names — an intent-only lane, or one the applicability no longer covers. */
  pending: boolean;
}

/**
 * Every perspective lane `ArcLaneGridMolecule` should render, given a schema catalog and the
 * template row's `applicability`: universal perspectives (`center_baseschema`) ∪ perspectives of
 * every schema the applicability's `type` clause names ∪ every perspective an intent already
 * names (`distinctIntentLanes`), in that order, deduped by `laneKey`. A lane backed by neither
 * the universal schema nor an applicable schema — only an intent names it — is `pending`
 * (arc-editor-canvas-v1.md's Lanes row). `applicability` lives on the `ArcTemplate` row, not the
 * definition this model wraps (`types/arc.d.ts` `ArcTemplate.applicability` vs
 * `ArcTemplateDefinition`), so it is a separate parameter rather than `model.unauthored`.
 *
 * `schemas` need only contain `center_baseschema` and the schemas the applicability's `type`
 * clause names; extras are ignored. When the applicability isn't a simple `type`-include
 * expression (e.g. no filter, or a `family` clause), no schema is treated as "applicable" and
 * only the universal + intent-named lanes are returned.
 */
export function resolveLanesForModel(
  model: ArcCanvasModel,
  schemas: Schema[],
  applicability: UnifiedFilters | null | undefined
): ArcLaneDefinition[] {
  const baseSchema = schemas.find((schema) => schema.system_name === 'center_baseschema');
  const universalKeys = new Set((baseSchema?.perspectives ?? []).map((p) => p.system_name));

  const classified = classifyApplicability(applicability);
  const applicableSchemas =
    classified.mode === 'include'
      ? schemas.filter((schema) => classified.sphereTypes.includes(schema.system_name))
      : [];
  const applicableKeys = new Set<string>();
  for (const schema of applicableSchemas) {
    for (const perspective of schema.perspectives ?? []) {
      applicableKeys.add(perspective.system_name);
    }
  }

  const seen = new Set<string>();
  const lanes: ArcLaneDefinition[] = [];
  const addLane = (laneKey: string) => {
    if (seen.has(laneKey)) return;
    seen.add(laneKey);
    lanes.push({ laneKey, pending: !universalKeys.has(laneKey) && !applicableKeys.has(laneKey) });
  };

  universalKeys.forEach(addLane);
  applicableKeys.forEach(addLane);
  distinctIntentLanes(model).forEach(addLane);

  return lanes;
}

/** Total unattributed-intent count across every stage — the Unattributed row's `count`. */
export function unattributedIntentCount(model: ArcCanvasModel): number {
  return model.stages.reduce((sum, stage) => sum + intentsInLane(stage, null).length, 0);
}

/**
 * The subject text for an `ArcActionCardMolecule` chip (`arc-lane.jsx`'s `a.subject`): the
 * system name / reference the action reads, resolved per action type. `undefined` when the
 * action carries no reference yet (a freshly dropped, unconfigured action — the card falls back
 * to its own "nothing chosen yet" copy).
 */
export function actionSubjectOf(action: StageActionDefinition): string | undefined {
  switch (action.type) {
    case 'set_aspect_value':
    case 'attach_aspect':
    case 'detach_aspect':
      return (action as { aspectSystemName?: string }).aspectSystemName || undefined;
    case 'deploy_approach':
      return (action as { approach?: { systemName?: string } }).approach?.systemName || undefined;
    case 'attach_status_factor':
      return (action as { factor?: { name?: { default?: string } } }).factor?.name?.default || undefined;
    default:
      return undefined;
  }
}

/** The action's automation tier, defaulting to `addAction`'s own default (`confirm`) when absent. */
export function actionTierOf(action: StageActionDefinition): AutomationTier {
  return (action as { tier?: AutomationTier }).tier ?? 'confirm';
}

/** `suggest`/`draft`/`confirm` hold for a person; `delegated`/`automatic` run on their own (`acHoldsForPerson`). */
export function actionTierHoldsForPerson(tier: AutomationTier): boolean {
  return tier === 'suggest' || tier === 'draft' || tier === 'confirm';
}

/* ------------------------------------------------------------------ */
/*  Arc-level inspector (D24): name/description, key dates, settings   */
/* ------------------------------------------------------------------ */

export interface ArcMetaUpdates {
  name?: string;
  description?: string;
}

/** Updates the arc's own name/description — no `selection` concept guards this (unlike a
 * stage/intent/action edit), since there is exactly one arc per model. */
export function updateArcMeta(model: ArcCanvasModel, updates: ArcMetaUpdates): ArcCanvasModel {
  return {
    ...model,
    ...(updates.name !== undefined ? { name: updates.name } : {}),
    ...(updates.description !== undefined ? { description: updates.description } : {}),
  };
}

const DEFAULT_ARC_SETTINGS: NonNullable<ArcTemplateDefinition['arcSettings']> = {
  allowManualCancellation: false,
  reopenable: false,
  autoComplete: false,
};

/** `arcSettings`, defaulted — a template authored before this field existed carries none. */
export function arcSettingsOf(model: ArcCanvasModel): NonNullable<ArcTemplateDefinition['arcSettings']> {
  return { ...DEFAULT_ARC_SETTINGS, ...model.unauthored.arcSettings };
}

export type ArcSettingsUpdates = Partial<NonNullable<ArcTemplateDefinition['arcSettings']>>;

export function updateArcSettings(model: ArcCanvasModel, updates: ArcSettingsUpdates): ArcCanvasModel {
  return {
    ...model,
    unauthored: { ...model.unauthored, arcSettings: { ...arcSettingsOf(model), ...updates } },
  };
}

export interface ArcKeyDateRow {
  name: string;
  expression: string;
}

/** `model.keyDates` as an ordered row list for the inspector's list editor (`Object.entries`
 * preserves insertion order, so rows do not reshuffle as they're edited). */
export function keyDateRowsOf(model: ArcCanvasModel): ArcKeyDateRow[] {
  return Object.entries(model.keyDates).map(([name, def]) => ({
    name,
    expression: def.expression ?? '',
  }));
}

/** Appends a blank key date under a fresh, unused name (`key_date_1`, `key_date_2`, …) — always
 * marks the draft dirty, matching `addStage`'s "a new row always changes the model" convention. */
export function addKeyDate(model: ArcCanvasModel): ArcCanvasModel {
  let n = Object.keys(model.keyDates).length + 1;
  let name = `key_date_${n}`;
  while (name in model.keyDates) {
    n += 1;
    name = `key_date_${n}`;
  }
  return {
    ...model,
    keyDates: {
      ...model.keyDates,
      [name]: { expression: '' },
    },
  };
}

export interface ArcKeyDateUpdates {
  /** Renames the row — a no-op (returns the same model reference) when the target name is
   * already taken by a different key date, so two rows never collapse onto one silently. */
  name?: string;
  expression?: string;
}

export function updateKeyDate(model: ArcCanvasModel, name: string, updates: ArcKeyDateUpdates): ArcCanvasModel {
  const existing = model.keyDates[name];
  if (!existing) return model;
  const nextName = updates.name?.trim();
  const renaming = nextName !== undefined && nextName !== '' && nextName !== name;
  if (renaming && nextName! in model.keyDates) return model;

  const nextEntry: KeyDateDefinition = {
    ...existing,
    ...(updates.expression !== undefined ? { expression: updates.expression } : {}),
  };

  const keyDates = { ...model.keyDates };
  if (renaming) {
    delete keyDates[name];
    keyDates[nextName!] = nextEntry;
  } else {
    keyDates[name] = nextEntry;
  }
  return { ...model, keyDates };
}

export function removeKeyDate(model: ArcCanvasModel, name: string): ArcCanvasModel {
  if (!(name in model.keyDates)) return model;
  const keyDates = { ...model.keyDates };
  delete keyDates[name];
  return { ...model, keyDates };
}
